import { ChevronLeft, X } from 'lucide-react';
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import './SettingsLayout.css';

export interface SettingsSection {
  id: string;
  label: string;
  /** Überschrift der Gruppe in der Navigation (aufeinanderfolgende Einträge gleicher Gruppe). */
  group?: string;
  /** data-testid des Navigations-Eintrags (Standard: `settings-tab-<id>`). */
  testId?: string;
  /** Gefährliche Aktion (rot). */
  danger?: boolean;
}

interface Props {
  title: string;
  sections: SettingsSection[];
  active: string;
  onSelect: (id: string) => void;
  onClose: () => void;
  children: ReactNode;
  /** data-testid der Wurzel (Standard `settings-layout`). */
  testId?: string;
  /**
   * Mobil gesteuert (z. B. über die Route): `nav` zeigt die Navigation, `content` den Inhalt.
   * Ohne Angabe schaltet das Layout selbst um („Zurück" → Navigation, Auswahl → Inhalt).
   */
  mobileView?: 'nav' | 'content';
  /** Wird bei „Zurück" (mobil) aufgerufen, wenn `mobileView` gesteuert ist. */
  onMobileBack?: () => void;
}

/**
 * Vollbild-Einstellungen (Nutzer-, Server- und Kanaleinstellungen): Navigation links, Inhalt rechts,
 * Schließen-Knopf (auch Escape). Schmal (≤ 820 px): erst die Navigation als Liste, nach Auswahl der Inhalt mit „Zurück".
 */
export function SettingsLayout({ title, sections, active, onSelect, onClose, children, testId = 'settings-layout', mobileView, onMobileBack }: Props) {
  const [mobileNavState, setMobileNav] = useState(false);
  const mobileNav = mobileView ? mobileView === 'nav' : mobileNavState;
  const contentRef = useRef<HTMLElement>(null);
  const navId = useId();

  useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0 });
  }, [active]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Offene Dialoge/Menüs fangen Escape selbst (capture + stopPropagation).
      onClose();
    };
    // Window (Bubble-Phase): kommt als Letztes an, Dialoge haben Escape dann schon abgefangen.
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const select = (id: string) => {
    setMobileNav(false);
    onSelect(id);
    contentRef.current?.focus();
  };

  const activeLabel = sections.find((s) => s.id === active)?.label ?? '';
  const back = () => {
    if (mobileView) onMobileBack?.();
    else setMobileNav(true);
  };

  return (
    <div className={`settings-layout ${mobileNav ? 'mobile-nav' : ''}`} data-testid={testId} data-view={mobileNav ? 'nav' : 'content'} role="region" aria-label={title}>
      <nav className="settings-nav scroll" aria-labelledby={navId} id={`${navId}-nav`}>
        <div className="settings-nav-inner">
          <h2 className="settings-nav-title truncate" id={navId}>
            {title}
          </h2>
          <ul role="list">
            {sections.map((s, i) => {
              const showGroup = s.group && s.group !== sections[i - 1]?.group;
              return (
                <Fragment key={s.id}>
                  {showGroup && (
                    <li className="settings-nav-group" aria-hidden="true">
                      {s.group}
                    </li>
                  )}
                  {!s.group && i > 0 && sections[i - 1]?.group && <li className="settings-nav-sep" role="separator" />}
                  <li>
                    <button
                      className={`settings-nav-item ${s.danger ? 'danger' : ''}`}
                      aria-current={s.id === active ? 'page' : undefined}
                      onClick={() => select(s.id)}
                      data-testid={s.testId ?? `settings-tab-${s.id}`}
                    >
                      {s.label}
                    </button>
                  </li>
                </Fragment>
              );
            })}
          </ul>
        </div>
      </nav>
      <main className="settings-content scroll" ref={contentRef} tabIndex={-1} aria-label={activeLabel}>
        <div className="settings-mobile-bar">
          <button className="btn btn-ghost btn-sm" onClick={back} aria-controls={`${navId}-nav`} data-testid="settings-mobile-back">
            <ChevronLeft size={18} /> {title}
          </button>
        </div>
        <div className="settings-content-inner">{children}</div>
        <div className="settings-close">
          <button className="settings-close-btn" onClick={onClose} aria-label="Einstellungen schließen" data-testid="settings-close">
            <X size={20} />
          </button>
          <span className="settings-close-hint" aria-hidden="true">
            ESC
          </span>
        </div>
      </main>
    </div>
  );
}

/** Überschrift + optionale Beschreibung eines Einstellungs-Abschnitts. */
export function SettingsHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <header className="settings-section-header">
      <h1>{title}</h1>
      {children && <p className="muted">{children}</p>}
    </header>
  );
}

/** Leiste „Ungespeicherte Änderungen" am unteren Rand. */
export function SaveBar({ dirty, busy, onSave, onReset, testId = 'save-bar' }: { dirty: boolean; busy?: boolean; onSave: () => void; onReset: () => void; testId?: string }) {
  if (!dirty) return null;
  return (
    <div className="settings-savebar" role="region" aria-label="Ungespeicherte Änderungen" data-testid={testId}>
      <span>Achtung – du hast ungespeicherte Änderungen!</span>
      <span className="spacer" />
      <button className="btn btn-ghost btn-sm" onClick={onReset} disabled={busy} data-testid={`${testId}-reset`}>
        Zurücksetzen
      </button>
      <button className="btn btn-primary btn-sm" onClick={onSave} disabled={busy} data-testid={`${testId}-save`}>
        {busy ? 'Speichern …' : 'Änderungen speichern'}
      </button>
    </div>
  );
}
